import { UNSET_PROPERTY_KEY } from '@blaze-shared/elements-domain';
import { type AppExtendedThemeValue } from '@blaze-shared/theming-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { determineMergeType, MergeType } from './merge-type';
import { type MergeResult, type PropsSource } from './props-source';

function getKeysToMerge(sources: any[]): Set<string> {
  const result = new Set<string>();
  for (const source of sources) {
    const keys = Object.keys(source);
    for (const key of keys) {
      result.add(key);
    }
  }
  return result;
}

function mergeRecursive(
  sources: any[],
  themeValue: AppExtendedThemeValue | undefined,
): MergeResult {
  const mergeType = determineMergeType(sources[0]);
  if (mergeType !== MergeType.OTHER) {
    for (const source of sources) {
      if (determineMergeType(source) === mergeType) {
        continue;
      }
      return mergeOthers(sources, themeValue);
    }
  }
  if (mergeType === MergeType.RECORD) {
    return mergeRecords(sources, themeValue);
  } else {
    return mergeOthers(sources, themeValue);
  }
}

function mergeRecords(sources: any[], themeValue: AppExtendedThemeValue | undefined): MergeResult {
  const result: MergeResult = {};
  const keys = getKeysToMerge(sources);

  for (const key of keys) {
    const keySources: MergeResult[] = [];

    for (const source of sources) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        keySources.push(source[key]);
      }
    }

    result[key] = mergeRecursive(keySources, themeValue);
  }

  return result;
}

function mergeOthers(sources: any[], themeValue: AppExtendedThemeValue | undefined): any {
  let lastSource = sources.at(-1);

  /**
   * Unset can be set only in element overrides.
   * If it is present, then use value previous element override.
   */
  if (lastSource === UNSET_PROPERTY_KEY) {
    lastSource = sources.at(-3);
  }

  if (lastSource === undefined) {
    return undefined;
  }

  return resolveThemeValue(lastSource, themeValue);
}

function resolveThemeValue(value: unknown, themeValue: AppExtendedThemeValue | undefined): unknown {
  if (!themeValue || typeof value !== 'string') {
    return value;
  }

  if (!value.startsWith('$t.')) {
    return value;
  }

  const themeValueKey = value.substring(3);
  if (themeValueKey.startsWith('palettes')) {
    return getValueByPath(themeValue, themeValueKey);
  }

  return themeValue[themeValueKey as keyof AppExtendedThemeValue];
}

export function mergeProps<T = unknown>(
  sources: PropsSource[],
  themeValue?: AppExtendedThemeValue,
): T {
  return mergeRecursive(
    sources.map((s) => s.value ?? {}),
    themeValue,
  );
}
